import { 
  collection, 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc,
  query, 
  where, 
  getDocs,
  serverTimestamp,
  orderBy,
} from 'firebase/firestore';
import { db } from '../../firebase';

// Prediction Service for Firestore operations
export const predictionService = {
  /**
   * Create or update a prediction for a race
   * @param {string} userId - User's Firebase UID
   * @param {string} raceId - Race identifier (e.g., "2024_round_1")
   * @param {Object} predictionData - Prediction data
   * @returns {Promise<Object>} Created/updated prediction
   */
  createPrediction: async (userId, raceId, predictionData) => {
    try {
      const { pole, first, second, third, fourth, fastestLap } = predictionData;

      // Validate required fields
      if (!pole || !first || !second || !third || !fourth || !fastestLap) {
        throw new Error('All prediction fields are required');
      }

      // Check if prediction already exists for this race
      const existingPrediction = await predictionService.getPrediction(userId, raceId);

      if (existingPrediction) {
        // Check if already scored
        if (existingPrediction.isScored) {
          throw new Error('Cannot update prediction after race has been scored');
        }

        // Update existing prediction
        const predictionRef = doc(db, 'predictions', existingPrediction.id);
        await updateDoc(predictionRef, {
          pole,
          first,
          second,
          third,
          fourth,
          fastestLap,
          updatedAt: serverTimestamp(),
        });

        console.log('✅ Prediction updated:', existingPrediction.id);
        return { id: existingPrediction.id, ...existingPrediction, ...predictionData };
      } else {
        // Create new prediction
        const predictionRef = doc(collection(db, 'predictions'));
        const predictionDoc = {
          userId,
          raceId,
          pole,
          first,
          second,
          third,
          fourth,
          fastestLap,
          pointsEarned: 0,
          isScored: false,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        };

        await setDoc(predictionRef, predictionDoc);

        console.log('✅ Prediction created:', predictionRef.id);
        return { id: predictionRef.id, ...predictionDoc };
      }
    } catch (error) {
      console.error('❌ Error creating/updating prediction:', error);
      throw error;
    }
  },

  /**
   * Get a specific prediction for a race
   * @param {string} userId - User's Firebase UID
   * @param {string} raceId - Race identifier
   * @returns {Promise<Object|null>} Prediction data or null
   */
  getPrediction: async (userId, raceId) => {
    try {
      const predictionsQuery = query(
        collection(db, 'predictions'),
        where('userId', '==', userId),
        where('raceId', '==', raceId)
      );

      const querySnapshot = await getDocs(predictionsQuery);

      if (querySnapshot.empty) {
        console.log('ℹ️ No prediction found for race:', raceId);
        return null;
      }

      const predictionDoc = querySnapshot.docs[0];
      const predictionData = { id: predictionDoc.id, ...predictionDoc.data() };

      console.log('✅ Prediction retrieved for race:', raceId);
      return predictionData;
    } catch (error) {
      console.error('❌ Error getting prediction:', error);
      throw error;
    }
  },

  /**
   * Get all predictions for a user
   * @param {string} userId - User's Firebase UID
   * @returns {Promise<Array>} Array of predictions
   */
  getUserPredictions: async (userId) => {
    try {
      const predictionsQuery = query(
        collection(db, 'predictions'),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(predictionsQuery);
      const predictions = [];

      querySnapshot.forEach((doc) => {
        predictions.push({ id: doc.id, ...doc.data() });
      });

      console.log('✅ Retrieved predictions:', predictions.length);
      return predictions;
    } catch (error) {
      console.error('❌ Error getting user predictions:', error);
      throw error;
    }
  },

  /**
   * Get latest unscored prediction
   * @param {string} userId - User's Firebase UID
   * @returns {Promise<Object|null>} Latest prediction or null
   */
  getLatestPrediction: async (userId) => {
    try {
      const predictionsQuery = query(
        collection(db, 'predictions'),
        where('userId', '==', userId),
        where('isScored', '==', false),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(predictionsQuery);

      if (querySnapshot.empty) {
        console.log('ℹ️ No unscored predictions found');
        return null;
      }

      const predictionDoc = querySnapshot.docs[0];
      const predictionData = { id: predictionDoc.id, ...predictionDoc.data() };

      console.log('✅ Latest prediction retrieved');
      return predictionData;
    } catch (error) {
      console.error('❌ Error getting latest prediction:', error);
      throw error;
    }
  },

  /**
   * Validate prediction data
   * @param {Object} predictionData - Prediction to validate
   * @returns {Object} Validation result
   */
  validatePrediction: (predictionData) => {
    const errors = [];

    // Check all fields are present
    if (!predictionData.pole) errors.push('Pole position is required');
    if (!predictionData.first) errors.push('1st place is required');
    if (!predictionData.second) errors.push('2nd place is required');
    if (!predictionData.third) errors.push('3rd place is required');
    if (!predictionData.fourth) errors.push('4th place is required');
    if (!predictionData.fastestLap) errors.push('Fastest lap is required');

    return {
      isValid: errors.length === 0,
      errors,
    };
  },
};

export default predictionService;

